/**
 * Command schemas (Zod 4). Every command the reducer accepts is listed here;
 * the server validates incoming frames against `CommandSchema`.
 */
import { z } from 'zod';

const role = z.enum(['EM', 'SURG', 'PHYS', 'PSYCH', 'ICU']);
const pid = z.string().min(1).max(16);
const routeCode = z.enum(['S', 'M', 'P', 'G', 'X']);

// ─── seat commands ───
export const ActionCommandSchema = z.discriminatedUnion('type', [
  z.strictObject({ type: z.literal('ASSESS'), p: pid }),
  z.strictObject({ type: z.literal('OPINION'), p: pid }),
  z.strictObject({ type: z.literal('SCAN'), p: pid }),
  z.strictObject({ type: z.literal('FREE_ASSESS'), p: pid }),
  z.strictObject({ type: z.literal('SECOND_OPINION'), p: pid }),
  z.strictObject({ type: z.literal('STABILISE'), p: pid }),
  z.strictObject({ type: z.literal('START_ROUTE'), p: pid, code: routeCode }),
  z.strictObject({ type: z.literal('TREAT'), p: pid }),
  z.strictObject({ type: z.literal('OPERATE'), p: pid, theatre: z.literal([0, 1]).optional() }),
  z.strictObject({ type: z.literal('ANAESTHETISE'), theatre: z.literal([0, 1]).optional() }),
  z.strictObject({ type: z.literal('SUPPORT'), p: pid }),
  z.strictObject({ type: z.literal('STEP_DOWN'), p: pid }),
  z.strictObject({ type: z.literal('DISCHARGE'), p: pid }),
  z.strictObject({ type: z.literal('EARLY_DISCHARGE'), p: pid }),
  z.strictObject({ type: z.literal('USE_LOCUM'), p: pid, action: z.enum(['ASSESS', 'STABILISE', 'TREAT', 'DISCHARGE']) }),
  z.strictObject({ type: z.literal('REFER'), p: pid, to: role }),
  z.strictObject({ type: z.literal('RED_BLANKET'), p: pid }),
  z.strictObject({ type: z.literal('OPEN_CARD'), p: pid, to: role }),
  z.strictObject({ type: z.literal('CLOSE_CARD'), p: pid, to: role }),
  z.strictObject({ type: z.literal('RESOLVE_INTERRUPT'), i: z.string().max(16), p: pid.optional() }),
  z.strictObject({ type: z.literal('CALL_FOR_HELP') }),
  z.strictObject({ type: z.literal('BOOK'), kind: z.enum(['LOCUM', 'THEATRE', 'BED_MEETING']) }),
  z.strictObject({ type: z.literal('TIME_OUT') }),
  z.strictObject({ type: z.literal('ALLOCATE'), slot: z.enum(['scanner0', 'scanner1', 'theatre']), to: role.nullable() }),
  z.strictObject({ type: z.literal('SET_DIVERT'), on: z.boolean() }),
  z.strictObject({ type: z.literal('EVENT_CHOICE'), choice: z.enum(['COMPLY', 'CHALLENGE']) }),
  z.strictObject({ type: z.literal('PASS_LEAD'), to: role }),
  z.strictObject({ type: z.literal('ASSIGN_COVER'), to: role }),
  z.strictObject({ type: z.literal('MARK_DISPUTED'), note: z.string().max(200) }),
  z.strictObject({ type: z.literal('LEND_STAFF'), to: role, n: z.number().int().min(1).max(10) }),
  z.strictObject({ type: z.literal('PIN_STAFF'), p: pid, pinned: z.boolean() }),
  z.strictObject({ type: z.literal('ORDER_BLOOD') }),
]);
export type ActionCommand = z.infer<typeof ActionCommandSchema>;

const draftRevision = { turn: z.number().int().min(1), expectedRevision: z.number().int().min(0) };
export const MAX_DRAFT_ACTIONS = 40;
export const DraftCommandSchema = z.discriminatedUnion('type', [
  z.strictObject({ type: z.literal('SET_DRAFT'), ...draftRevision, actions: z.array(z.strictObject({ id: z.string().min(1).max(64), cmd: ActionCommandSchema, priority: z.literal([1, 2, 3]).optional() })).max(MAX_DRAFT_ACTIONS) }),
  z.strictObject({ type: z.literal('SET_READY'), ...draftRevision, ready: z.boolean() }),
  z.strictObject({ type: z.literal('RESPOND_REFERRAL'), ...draftRevision, sender: role, actionId: z.string().min(1).max(64), offerRevision: z.number().int().min(1), mode: z.enum(['BED', 'OVERFLOW']).nullable() }),
]);
export type DraftCommand = z.infer<typeof DraftCommandSchema>;
export const SeatCommandSchema = z.discriminatedUnion('type', [...ActionCommandSchema.options, ...DraftCommandSchema.options]);
export type SeatCommand = z.infer<typeof SeatCommandSchema>;

// ─── faculty / clock / system commands ───
export const ControlCommandSchema = z.discriminatedUnion('type', [
  z.strictObject({ type: z.literal('SEAT_CLAIM'), table: z.number().int(), role, name: z.string().min(1).max(40), bot: z.boolean() }),
  z.strictObject({ type: z.literal('SEAT_RELEASE'), table: z.number().int(), role }),
  z.strictObject({ type: z.literal('SET_BOT'), table: z.number().int(), role, bot: z.boolean() }),
  z.strictObject({ type: z.literal('START_SHIFT') }),
  z.strictObject({ type: z.literal('ADVANCE_PHASE'), from: z.strictObject({ turn: z.number().int(), phase: z.string() }) }),
  z.strictObject({ type: z.literal('PAUSE') }),
  z.strictObject({ type: z.literal('RESUME') }),
  z.strictObject({ type: z.literal('EXTEND'), ms: z.number().int().min(1000).max(600000) }),
  z.strictObject({ type: z.literal('HOLD'), on: z.boolean() }),
  z.strictObject({ type: z.literal('INJECT'), event: z.string().max(4), when: z.enum(['now', 'next']) }),
  z.strictObject({ type: z.literal('BROADCAST'), text: z.string().max(140).nullable() }),
  z.strictObject({ type: z.literal('RESTOCK'), table: z.number().int(), resource: z.enum(['staff', 'vents', 'blood']), n: z.number().int().min(-20).max(20), role: role.optional() }),
  z.strictObject({ type: z.literal('END_SHIFT') }),
  z.strictObject({ type: z.literal('SET_RING'), order: z.array(z.number().int()) }),
  z.strictObject({ type: z.literal('CORRECT_RESTORE_ACTION'), table: z.number().int(), role, n: z.number().int().min(1).max(5), reason: z.string().max(200) }),
  z.strictObject({ type: z.literal('CORRECT_MOVE'), p: pid, role, bed: z.union([z.number().int(), z.literal('corridor')]), reason: z.string().max(200) }),
  z.strictObject({ type: z.literal('CORRECT_CLOCK'), p: pid, ticks: z.number().int().min(0).max(8), reason: z.string().max(200) }),
  z.strictObject({ type: z.literal('CORRECT_PROMPT'), table: z.number().int(), prompt: z.string().max(16), answer: z.string().max(40), reason: z.string().max(200) }),
]);
export type ControlCommand = z.infer<typeof ControlCommandSchema>;

export type Command = SeatCommand | ControlCommand;

export const SEAT_COMMAND_TYPES = new Set<string>(SeatCommandSchema.options.map((o) => o.shape.type.value));

export function isSeatCommand(c: Command): c is SeatCommand {
  return SEAT_COMMAND_TYPES.has(c.type);
}
